// frontend/src/pages/settings/users-roles/PendingTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { CheckCircle, RefreshCw } from 'lucide-react';
import { KEY_LEVEL_ICON, CATALOG } from '../../../lib/rbac';
import { rbacApi } from './api';
import type { PendingRow, RoleSummary } from './types';

export function PendingTab({ onChanged }: { onChanged?: () => void }) {
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [picks, setPicks] = useState<Record<string, Set<number>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [p, r] = await Promise.all([rbacApi.pending(), rbacApi.listRoles()]);
    if (p.success && p.data) setRows(p.data); else setError(p.error || 'No se pudieron cargar los pendientes');
    if (r.success && r.data) setRoles(r.data.filter((x) => !x.is_admin));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (cap: string, roleId: number) => {
    const s = new Set(picks[cap] ?? []);
    if (s.has(roleId)) s.delete(roleId); else s.add(roleId);
    setPicks({ ...picks, [cap]: s });
  };

  const decide = async (cap: string, nobody: boolean) => {
    const chosen = nobody ? [] : [...(picks[cap] ?? [])].map((role_id) => ({ role_id, granted: true }));
    const res = await rbacApi.decide(cap, chosen);
    if (res.success) { await load(); onChanged?.(); } else setError(res.error || 'No se pudo guardar la decisión');
  };

  if (loading) return <div className="flex justify-center py-8 text-[var(--color-text-muted)]"><RefreshCw className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-[var(--color-text-muted)]">
        Funciones nuevas que aparecieron con una actualización y que ningún rol recibe hasta que lo decidas. Las funciones comunes («ver»/«operar») ya se heredaron solas; acá solo aparecen las llaves especiales.
      </p>
      {error && <div className="px-3 py-2 rounded-lg text-xs bg-red-500/10 border border-red-500/30 text-red-600">{error}</div>}
      {rows.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-green-600"><CheckCircle size={16} /> No hay permisos pendientes de decisión.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const areaLabel = CATALOG.areas.find((a) => a.key === row.area)?.label ?? row.area;
            return (
              <div key={row.capability} className="border border-[var(--color-border)] rounded-lg p-3 space-y-2">
                <div className="text-sm font-medium text-[var(--color-text)]">
                  {KEY_LEVEL_ICON[row.level] ?? ''} {row.label}
                  <span className="ml-2 text-[10px] text-[var(--color-text-muted)]">{areaLabel} · visto el {new Date(row.first_seen_at).toLocaleDateString()}</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {roles.map((r) => (
                    <label key={r.id} className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={picks[row.capability]?.has(r.id) ?? false} onChange={() => toggle(row.capability, r.id)} /> {r.name}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => decide(row.capability, false)} className="px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] text-white">Guardar decisión</button>
                  <button onClick={() => decide(row.capability, true)} className="px-3 py-1.5 text-xs rounded border border-[var(--color-border)]">Nadie por ahora</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
